import type { VisualField } from '../../../types/wizardTypes';
import { getFieldKind } from './fieldTypeKind';

/**
 * Convert visual fields to JSON Schema object.
 */
export function fieldsToSchema(fields: VisualField[]): object {
  if (fields.length === 0) return {};

  const properties: Record<string, object> = {};
  const required: string[] = [];

  for (const field of fields) {
    if (!field.name.trim()) continue;

    const kind = getFieldKind(field.type);
    const prop: Record<string, unknown> = {
      type: kind === 'enum' || kind === 'date' || kind === 'datetime' || kind === 'image' ? 'string' : kind,
    };
    if (kind === 'date') {
      prop.format = 'date';
    } else if (kind === 'datetime') {
      prop.format = 'date-time';
    } else if (kind === 'image') {
      prop.contentEncoding = 'base64';
      prop.contentMediaType = 'image/*';
    }
    if (field.label) prop.title = field.label;
    if (field.description) prop.description = field.description;
    if (field.example) prop.example = field.example;
    if (field.defaultValue !== undefined && field.defaultValue !== '') {
      if (kind === 'boolean') {
        prop.default = field.defaultValue === 'true';
      } else if (kind === 'number' || kind === 'integer') {
        prop.default = Number(field.defaultValue);
      } else {
        prop.default = field.defaultValue;
      }
    }
    if (field.enumOptions && field.enumOptions.length > 0) {
      prop.enum = field.enumOptions.map(o => o.value);
      if (field.enumOptions.some(o => o.value !== o.key)) {
        prop.enumNames = field.enumOptions.map(o => o.key);
      }
    }

    properties[field.name] = prop;
    if (field.required) required.push(field.name);
  }

  return {
    type: 'object',
    properties,
    ...(required.length > 0 ? { required } : {}),
  };
}

/**
 * Convert a JSON Schema object back to visual fields (for edit-mode hydration).
 * `isMssql` picks a representative SQL type per kind (e.g. `int`, `varchar`) instead of
 * the generic type - JSON Schema alone can't distinguish e.g. `smallint` from `bigint`,
 * so the exact SQL type isn't round-tripped, only its kind.
 */
export function schemaToFields(schema: unknown, isMssql = false): VisualField[] {
  if (!schema || typeof schema !== 'object') return [];

  const s = schema as Record<string, unknown>;
  const properties = s.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return [];

  const requiredList = Array.isArray(s.required) ? (s.required as string[]) : [];

  return Object.entries(properties).map(([key, prop]) => {
    const hasEnum = Array.isArray(prop.enum) && prop.enum.length > 0;
    const enumValues = hasEnum ? (prop.enum as string[]) : undefined;
    const enumNames = Array.isArray(prop.enumNames) ? (prop.enumNames as string[]) : undefined;
    return {
      id: crypto.randomUUID(),
      name: key,
      label: typeof prop.title === 'string' && prop.title.trim() ? prop.title : labelFromName(key),
      type: hasEnum ? 'enum' as const : normalizeType(prop, isMssql),
      description: (prop.description as string) || '',
      required: requiredList.includes(key),
      example: prop.example != null ? String(prop.example) : '',
      defaultValue: prop.default != null ? String(prop.default) : undefined,
      enumOptions: enumValues?.map((value, i) => ({ key: enumNames?.[i] ?? value, value })),
      nameIsCustom: true,
    };
  });
}

function normalizeType(prop: Record<string, unknown>, isMssql: boolean): VisualField['type'] {
  const t = prop.type;
  const contentMediaType = prop.contentMediaType as string | undefined;
  const isDate = t === 'string' && prop.format === 'date';
  const isDatetime = t === 'string' && prop.format === 'date-time';
  const isImage = t === 'string' && contentMediaType?.startsWith('image/');

  if (isMssql) {
    if (isDate) return 'date';
    if (isDatetime) return 'datetime';
    if (t === 'integer') return 'int';
    if (t === 'number') return 'decimal';
    if (t === 'boolean') return 'bit';
    return 'varchar';
  }

  if (isDate) return 'date';
  if (isDatetime) return 'date';
  if (isImage) return 'image';
  if (t === 'string' || t === 'number' || t === 'boolean' || t === 'integer') return t;
  return 'string';
}

function labelFromName(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .replace(/\b[a-z]/g, c => c.toUpperCase())
    .trim();
}
